import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, GripVertical, Trophy, XCircle, X } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const STAGE_COLORS = [
  '#9CA3AF', '#3B82F6', '#8B5CF6', '#F59E0B', '#10B981',
  '#EF4444', '#EC4899', '#06B6D4', '#F97316', '#6366F1',
];

function SortableStageRow({ stage, onUpdate, onDelete, disabled }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stage.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
      >
        <GripVertical className="w-4 h-4" />
      </button>

      <input
        type="color"
        value={stage.color}
        onChange={(e) => onUpdate(stage.id, { color: e.target.value })}
        className="w-8 h-8 rounded border border-gray-200 cursor-pointer"
      />

      <input
        type="text"
        value={stage.name}
        onChange={(e) => onUpdate(stage.id, { name: e.target.value })}
        className="flex-1 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 rounded-md bg-transparent dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
        placeholder="Stage name"
      />

      <button
        onClick={() => onUpdate(stage.id, { is_win_stage: !stage.is_win_stage, is_loss_stage: false })}
        className={`p-1.5 rounded-md text-sm ${
          stage.is_win_stage
            ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
        title="Win stage"
      >
        <Trophy className="w-4 h-4" />
      </button>

      <button
        onClick={() => onUpdate(stage.id, { is_loss_stage: !stage.is_loss_stage, is_win_stage: false })}
        className={`p-1.5 rounded-md text-sm ${
          stage.is_loss_stage
            ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
        title="Loss stage"
      >
        <XCircle className="w-4 h-4" />
      </button>

      {!disabled && (
        <button
          onClick={() => onDelete(stage.id)}
          className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
          title="Delete stage"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

let tempIdCounter = 0;

export default function PipelineEditor({
  pipeline,
  onSave,
  onClose,
  isCreating = false,
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [stages, setStages] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (pipeline) {
      setName(pipeline.name || '');
      setDescription(pipeline.description || '');
      setStages(
        (pipeline.stages || [])
          .sort((a, b) => a.position - b.position)
          .map((s) => ({ ...s }))
      );
    } else if (isCreating) {
      setName('');
      setDescription('');
      setStages([
        { id: `new-${++tempIdCounter}`, name: 'Lead', color: '#9CA3AF', position: 1, is_win_stage: false, is_loss_stage: false },
        { id: `new-${++tempIdCounter}`, name: 'Qualified', color: '#3B82F6', position: 2, is_win_stage: false, is_loss_stage: false },
        { id: `new-${++tempIdCounter}`, name: 'Proposal', color: '#8B5CF6', position: 3, is_win_stage: false, is_loss_stage: false },
        { id: `new-${++tempIdCounter}`, name: 'Won', color: '#10B981', position: 4, is_win_stage: true, is_loss_stage: false },
        { id: `new-${++tempIdCounter}`, name: 'Lost', color: '#EF4444', position: 5, is_win_stage: false, is_loss_stage: true },
      ]);
    }
  }, [pipeline, isCreating]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      setStages((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);
        return newItems.map((item, index) => ({ ...item, position: index + 1 }));
      });
    }
  }, []);

  const handleUpdateStage = useCallback((id, updates) => {
    setStages((prev) =>
      prev.map((s) => (s.id === id ? { ...s, ...updates } : s))
    );
  }, []);

  const handleDeleteStage = useCallback((id) => {
    setStages((prev) => {
      const stage = prev.find((s) => s.id === id);
      if (stage && stage.deal_count > 0) {
        return prev;
      }
      return prev.filter((s) => s.id !== id).map((s, i) => ({ ...s, position: i + 1 }));
    });
  }, []);

  const handleAddStage = () => {
    const nextPosition = stages.length + 1;
    const colorIndex = (nextPosition - 1) % STAGE_COLORS.length;
    setStages((prev) => [
      ...prev,
      {
        id: `new-${++tempIdCounter}`,
        name: '',
        color: STAGE_COLORS[colorIndex],
        position: nextPosition,
        is_win_stage: false,
        is_loss_stage: false,
        deal_count: 0,
      },
    ]);
  };

  const handleSave = async () => {
    if (!name.trim()) return;
    if (stages.length === 0) return;

    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        description: description.trim() || null,
        stages: stages.map((s, i) => ({
          ...s,
          position: i + 1,
          id: s.id.startsWith('new-') ? undefined : s.id,
        })),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="absolute inset-0 z-20" onClick={onClose}></div>
      <div className="relative top-10 z-50 mx-auto p-6 border max-w-lg shadow-lg rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700 mb-10">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {isCreating ? 'Create Pipeline' : 'Edit Pipeline'}
          </h3>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Pipeline Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-transparent dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="e.g., Sales Pipeline"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-transparent dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-brand-500"
              placeholder="Optional description..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Stages
            </label>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={stages.map((s) => s.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {stages.map((stage) => (
                    <SortableStageRow
                      key={stage.id}
                      stage={stage}
                      onUpdate={handleUpdateStage}
                      onDelete={handleDeleteStage}
                      disabled={stage.deal_count > 0}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>

            <button
              onClick={handleAddStage}
              className="mt-3 flex items-center gap-2 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-brand-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Stage
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !name.trim() || stages.length === 0}
            className="px-4 py-2 text-sm text-white bg-brand-600 hover:bg-brand-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : isCreating ? 'Create Pipeline' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
